import { useContext, useEffect } from "react"
import { Color } from "three"
import type { Object3D } from "three"
import { FindMaterialApprox, FindObject } from "../../Common/Utils/ThreeTools"
import ModelElements from "../ModelElements"
import type { ProductModelProps } from "../ModelViewerProduct"
import ProductTextureContext from "../_contexts/ProductTextureContext"
import useGLB from "../_hooks/useGLB"
import type { HasStandardMaterial } from "../_hooks/useGLB"

const patchTypes = [
    "circle_2",
    "circle_2_5",
    "oval_2_75",
    "oval_3_5",
    "rectangle_2_75",
    "rectangle_3_5",
    "square_2",
    "square_2_5",
]

export default function SnapbackWithPrintedPatch(props: ProductModelProps) {
    const url = new URL('@resources/3d-models/glb/snapback-with-printed-patch.glb', import.meta.url);
    const model = useGLB(url)
    const elements = model.scene.children
    const [productTexture] = useContext(ProductTextureContext)

    // Control which patch is displayed
    useEffect(() => {
        for (const patchType of patchTypes) {
            const patchObject = FindObject(patchType, elements, false)
            if (!patchObject) continue
            patchObject.visible = patchType === props.variant
        }
    }, [elements, props.variant])

    const optionColor = props.options.get('color') ?? 'black'
    const color = {
        "white": {"cap": new Color(0xe9e1de), "bill": new Color(0xe9e1de), "heather": false},
        "black": {"cap": new Color(0x322c2e), "bill": new Color(0x322c2e), "heather": false},
        "heather_grey": {"cap": new Color(0xb9b3a7), "bill": new Color(0xb9b3a7), "heather": true},
        "dark_heather": {"cap": new Color(0x5c5451), "bill": new Color(0x5c5451), "heather": true},
        "navy": {"cap": new Color(0x24263b), "bill": new Color(0x24263b), "heather": false},
        "black_silver": {"cap": new Color(0x332d2f), "bill": new Color(0xabaaaf), "heather": false},
        "navy_red": {"cap": new Color(0x252235), "bill": new Color(0xbc051b), "heather": false},
        "royal": {"cap": new Color(0x222d61), "bill": new Color(0x222d61), "heather": false},
        "maroon": {"cap": new Color(0x672c37), "bill": new Color(0x672c37), "heather": false},
        "heather_black": {"cap": new Color(0xbcb5ad), "bill": new Color(0x211d1e), "heather": true},
    }[optionColor]
    
    useEffect(() => {
        if (!color) return
 
        let material = FindMaterialApprox('cap_out', elements)
        if(material) {
            material.color = color.cap
            material.aoMap = material.map
            material.aoMapIntensity = color.heather ? 2.0 : 0.5
        }

        material = FindMaterialApprox('cap_in', elements)
        if(material) material.color = color.cap

        material = FindMaterialApprox('bill_out', elements)
        if(material) {
            material.color = color.bill
            material.aoMap = material.map
            material.aoMapIntensity = color.heather ? 2.0 : 0.0
        }

        material = FindMaterialApprox('eyelet', elements)
        if(material) material.color = color.bill

        material = FindMaterialApprox('button', elements)
        if(material) material.color = color.bill

        material = FindMaterialApprox('plastic', elements)
        if(material) material.color = color.bill

        material = FindMaterialApprox('bill_in', elements)
        if(material) material.color = new Color(0x277b4a)
    }, [color, elements])

    // Apply lab texture
    useEffect(() => {
        const patchObject = FindObject(props.variant, elements, false)
        if (!patchObject) return

        const designMaterial = FindMaterialApprox(
            'design',
            patchObject.children as Array<Object3D & HasStandardMaterial>
        )

        if (designMaterial) {
            designMaterial.map = productTexture
            designMaterial.map.flipY = false
            designMaterial.map.needsUpdate = true
        }
    }, [elements, productTexture, props.variant])
    
    return <ModelElements scene={model.scene}/>
}
